import {
    Box,
    Button,
    TabPanels,
    Tabs,
    useToast
} from '@chakra-ui/react'
import { ModalBuild } from 'components/molecules'
import { Bar } from 'components/molecules/Bar'
import { ModalBuildHandle } from 'components/molecules/ModalBuild/interfaces'
import { TableBuild } from 'components/organisms'
import { useAuthContext } from 'contexts/auth'
import { useContractContext } from 'contexts/contract'
import { useInstitutionsContext } from 'contexts/institutions'
import { motion } from 'framer-motion'
import { useCacheFilters } from 'hooks/useCacheFilters'
import { useContracts } from 'hooks/useContracts'
import { useHideElement } from 'hooks/useHideElement'
import {
    ContractsOrderBy,
    LoteQueryDTO,
    LoteResponseDTO
} from 'models/ContractsQueryDTO'
import { ModalSignature } from 'pages/components/ModalSignature'
import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useDebounce } from 'utils/useDebounce'
import { FilterContract } from './Components/Filter/Filter'
import { FormFileDataProps } from './Components/Forms/FormFileData/interfaces'
import FormNewContract from './Components/Forms/FormNewContract'
import { NewContractProps } from './Components/Forms/FormNewContract/interfaces'
import { GeneratorFiles } from './Components/GeneratorFiles'
import ModalBatchUpload from './Components/ModalBatchUpload'
import StepsForm from './Components/StepsForm'
import { defaultLoaders } from './constants'
import { Lotes } from './Lotes/Lotes'
import { TabCustom, TabListCustom, TabPanelCustom } from './styles'

type ActionText = {
    0: string
    1: string
    2: string
}

const Contracts: React.FC = () => {
    const TAB_CONTRACTS = 0
    const TAB_LOTE_CONTRACT = 1
    const TAB_LOTE_IMAGE = 2

    const actionText = (option: 0 | 1 | 2) => {
        const options: ActionText = {
            [TAB_CONTRACTS]: 'Novo Contrato',
            [TAB_LOTE_CONTRACT]: 'Novo Lote Contratos',
            [TAB_LOTE_IMAGE]: 'Novo Lote Imagens'
        }

        return options[option] || 'Novo Contrato'
    }

    const toast = useToast()
    const [filterLotes, setFilterLotes] = useState('')

    const [pageLote, setPageLote] = useState<number>(1)
    const [limitLote, setLimitLote] = useState(10)
    const [totalPagesLote, setTotalPagesLote] = useState(1)

    const [loteData, setLoteData] = useState<LoteResponseDTO[]>([])

    const [contractTemp, setContractTemp] = useState<NewContractProps>(
        {} as NewContractProps
    )

    const [externalAdditiveTemp, setExternalAdditiveTemp] =
        useState<NewContractProps>({} as NewContractProps)

    const {
        newContract,
        loadingCreateBatch,
        setNewContract,
        setLoadingLote,
        setTypeContract,
        setFileData,
        getLote
    } = useContractContext()

    const { hasCacheFilters } = useCacheFilters()

    const {
        pageContracts,
        limitContracts,
        totalPagesContracts,
        fetchContracts,
        handleActionContracts,
        handleLimitContracts,
        handlePageContracts,
        loadingTableContracts,
        fetchContractsWithCache,
        handleFilterContracts,
        tableContracts,
        handleOrderByContracts,
        orderByContracts,
        filtersContracts
    } = useContracts()

    const { currentInstitution } = useInstitutionsContext()
    const { isBackoffice } = useAuthContext()

    const [clearInput, setClearInput] = useState<boolean>(false)
    const [tabIndex, setTabIndex] = useState(0)

    const addModalRef = useRef<ModalBuildHandle>(null)
    const modalRefNewContract = useRef<ModalBuildHandle>(null)
    const externalAdditiveStartModalRef = useRef<ModalBuildHandle>(null)
    const externalAdditiveFormModalRef = useRef<ModalBuildHandle>(null)
    const modalBatchUpload = useRef<ModalBuildHandle>(null)

    const handleAddModalOpen = (): void => {
        if (tabIndex === TAB_CONTRACTS) {
            setTypeContract('CREATE')
            addModalRef.current?.onOpen()
        } else {
            modalBatchUpload.current?.onOpen()
        }
    }

    const modalBatchUploadClose = (): void => {
        modalBatchUpload.current?.onClose()
    }

    const handleNewContractOpen = (value: NewContractProps): void => {
        modalRefNewContract.current?.onOpen()
        addModalRef.current?.onClose()
        setContractTemp(value)
    }

    const handleNewContractClose = (): void => {
        modalRefNewContract.current?.onClose()
        setFileData({} as FormFileDataProps)
    }

    const handleAddModalClose = (): void => {
        addModalRef.current?.onClose()
    }

    const handleExternalAdditiveStartOpen = (): void => {
        setTypeContract('EXTERNAL_ADDITIVE')
        externalAdditiveStartModalRef.current?.onOpen()
    }

    const handleExternalAdditiveStartClose = (): void => {
        externalAdditiveStartModalRef.current?.onClose()
        setTypeContract('CREATE')
    }

    const handleExternalAdditiveFormOpen = (
        value: NewContractProps
    ): void => {
        setExternalAdditiveTemp(value)
        externalAdditiveStartModalRef.current?.onClose()
        externalAdditiveFormModalRef.current?.onOpen()
    }

    const handleExternalAdditiveFormClose = (): void => {
        externalAdditiveFormModalRef.current?.onClose()
        setFileData({} as FormFileDataProps)
        setTypeContract('CREATE')
    }

    const handlePageLote = (value: number) => {
        setPageLote(value)
    }

    const handleLimitLote = (value: number) => {
        setPageLote(1)
        setLimitLote(value)
    }

    useEffect(() => {
        setTypeContract('CREATE')
    }, [])

    const clearFilterLote = () => {
        setClearInput(true)

        setTimeout(() => {
            setClearInput(false)
        }, 100)

        setFilterLotes('')
    }

    const selector = '[data-testid="bar-search"]'

    useHideElement(
        selector,
        tabIndex === TAB_CONTRACTS,
        tabIndex !== TAB_CONTRACTS
    )

    useEffect(() => {
        clearFilterLote()
        setLoteData([])
        setLoadingLote(true)
    }, [tabIndex])

    const handleSearch = useDebounce(
        (e: ChangeEvent<HTMLInputElement>) => {
            setLoadingLote(true)
            setPageLote(1)

            const value = e.target.value.toLowerCase()

            setFilterLotes(value)
        }
    )

    const handleFilterLote = (query?: LoteQueryDTO) => {
        getLote(
            {
                ...query,
                pagina: pageLote ?? 1,
                limite: limitLote
            },
            isBackoffice,
            currentInstitution?.id as string,
            tabIndex
        )
            .then(({ data, headers }) => {
                setLoteData(data)
                setTotalPagesLote(+headers['total-pages'])
            })
            .catch(() => {
                toast({
                    title: 'Sentimos muito!',
                    description:
                        'Ocorreu algum erro, tente novamente mais tarde.',
                    position: 'bottom-left',
                    status: 'error',
                    isClosable: true
                })
            })
            .finally(() => setLoadingLote(false))
    }

    const getDataContracts = () => {
        if (!hasCacheFilters) {
            fetchContracts({})
            return
        }

        fetchContractsWithCache()
    }

    useEffect(() => {
        const isContract = tabIndex === TAB_CONTRACTS

        if (
            currentInstitution?.id !== undefined &&
            isContract
        ) {
            getDataContracts()
        }
    }, [
        orderByContracts,
        currentInstitution?.id,
        tabIndex
    ])

    useEffect(() => {
        const isLote = [
            TAB_LOTE_IMAGE,
            TAB_LOTE_CONTRACT
        ].includes(tabIndex)

        if (isLote) {
            handleFilterLote(
                filterLotes
                    ? {
                          nomeArquivo: filterLotes
                      }
                    : {}
            )
        }
    }, [
        pageLote,
        limitLote,
        filterLotes,
        currentInstitution,
        loadingCreateBatch,
        tabIndex
    ])

    useEffect(() => {
        if (newContract) {
            handleAddModalOpen()
            setNewContract(false)
        }
    }, [newContract])

    const refModalSignature =
        useRef<ModalBuildHandle>(null)

    const signatureModalControllerRef =
        useRef<any>(null)

    const toogleModalSignature = (action: string) => {
        if (action === 'close') {
            refModalSignature.current?.onClose()
            return
        }

        modalBatchUpload.current?.onClose()
        refModalSignature.current?.onOpen()
    }

    const onSubmitModalSignature = async () => {
        try {
            await new Promise(resolve => {
                setTimeout(() => {
                    resolve(1000)
                })
            })

            toast({
                title: 'Imagem enviada com sucesso para a assinatura',
                description:
                    'Acompanhe através da Área do Contrato.',
                position: 'bottom-left',
                status: 'info',
                isClosable: true
            })
        } catch (e) {
            toast({
                title: 'Erro ao enviar imagem para assinatura.',
                description: 'Tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        } finally {
            toogleModalSignature('close')
            modalBatchUploadClose()
            signatureModalControllerRef.current?.initialState()
        }
    }

    return (
        <>
            {tabIndex === TAB_CONTRACTS && (
                <FilterContract
                    handleFilterContracts={
                        handleFilterContracts
                    }
                    loadingFilter={false}
                />
            )}

            <ModalBuild
                ref={modalBatchUpload}
                closeOnOverlayClick={false}
                title=" "
                size="2xl"
            >
                <ModalBatchUpload
                    tabIndex={tabIndex}
                    onClose={modalBatchUploadClose}
                    toogleWithSignature={
                        toogleModalSignature
                    }
                    withSignature
                />
            </ModalBuild>

            <ModalSignature
                refModal={refModalSignature}
                ref={signatureModalControllerRef}
                onCancel={() =>
                    toogleModalSignature('close')
                }
                onSubmit={onSubmitModalSignature}
                allowZip
            />

            {/* Modal Novo Contrato com UF e Chassi */}
            <ModalBuild
                ref={addModalRef}
                closeOnOverlayClick={false}
                title=" "
                size="sm"
                isCentered
            >
                <FormNewContract
                    onSubmitForm={data =>
                        handleNewContractOpen(data)
                    }
                    onCancelForm={handleAddModalClose}
                />
            </ModalBuild>

            {/* Identificação inicial do aditivo sem contrato local */}
            <ModalBuild
                ref={externalAdditiveStartModalRef}
                closeOnOverlayClick={false}
                title=" "
                size="md"
                isCentered
                onCancel={
                    handleExternalAdditiveStartClose
                }
            >
                <FormNewContract
                    title="Novo Aditivo"
                    type="EXTERNAL_ADDITIVE"
                    showDraftOption={false}
                    onSubmitForm={
                        handleExternalAdditiveFormOpen
                    }
                    onCancelForm={
                        handleExternalAdditiveStartClose
                    }
                />
            </ModalBuild>

            {/* StepForm Novo Contrato */}
            <ModalBuild
                ref={modalRefNewContract}
                closeOnOverlayClick={false}
                size="5xl"
                shut="white"
            >
                <StepsForm
                    onCancel={handleNewContractClose}
                    onSubmit={handleNewContractClose}
                    fetchContracts={fetchContracts}
                    temp={contractTemp}
                    type="CREATE"
                />
            </ModalBuild>

            {/* StepForm Novo Aditivo para contrato de origem externa */}
            <ModalBuild
                ref={externalAdditiveFormModalRef}
                closeOnOverlayClick={false}
                size="5xl"
                shut="white"
                onCancel={
                    handleExternalAdditiveFormClose
                }
            >
                <StepsForm
                    onCancel={
                        handleExternalAdditiveFormClose
                    }
                    onSubmit={
                        handleExternalAdditiveFormClose
                    }
                    fetchContracts={fetchContracts}
                    temp={externalAdditiveTemp}
                    type="EXTERNAL_ADDITIVE"
                />
            </ModalBuild>

            <Tabs
                isLazy
                data-testid="institution-tabs"
                colorScheme="primary"
                as={motion.div}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onChange={index => setTabIndex(index)}
            >
                <TabListCustom data-testid="institution-tab-list">
                    <TabCustom data-testid="institution-tab-item-view">
                        Contratos
                    </TabCustom>

                    <TabCustom
                        mr="47px"
                        data-testid="institution-tab-address"
                    >
                        Lotes Contratos
                    </TabCustom>

                    <TabCustom
                        mr="47px"
                        data-testid="institution-tab-address"
                    >
                        Lotes Imagens
                    </TabCustom>

                    {tabIndex !== TAB_CONTRACTS && (
                        <Bar
                            clearInput={clearInput}
                            titleDisable
                            isBackoffice={isBackoffice}
                            input={{
                                props: {
                                    w: '100%',
                                    variant: 'flushed',
                                    placeholder:
                                        'Buscar pelo nome do arquivo'
                                }
                            }}
                            action={{
                                text: actionText(
                                    tabIndex as 0 | 1 | 2
                                ),
                                props: {
                                    colorScheme: 'primary'
                                }
                            }}
                            onChangeInput={handleSearch}
                            onClickActions={
                                handleAddModalOpen
                            }
                            flex={1}
                            marginBottom={0}
                        />
                    )}

                    {tabIndex === TAB_CONTRACTS && (
                        <Box
                            display="flex"
                            gap="8px"
                            justifyContent="flex-end"
                            width="100%"
                        >
                            <Button
                                colorScheme="primary"
                                onClick={
                                    handleAddModalOpen
                                }
                            >
                                {actionText(
                                    tabIndex as 0 | 1 | 2
                                )}
                            </Button>                        
                            
                            

                            <GeneratorFiles
                                filters={filtersContracts}
                                pagination={{
                                    page: pageContracts,
                                    limit: limitContracts
                                }}
                            />
                        </Box>
                    )}
                </TabListCustom>

                <TabPanels>
                    <TabPanelCustom data-testid="institution-tab-panel-contract">
                        <Box
                            as={motion.div}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <TableBuild
                                header={
                                    tableContracts.header
                                }
                                body={
                                    tableContracts.body
                                }
                                ordenation={
                                    orderByContracts
                                }
                                action={
                                    tableContracts.action
                                }
                                loading={{
                                    status:
                                        loadingTableContracts,
                                    loader: defaultLoaders
                                }}
                                pagination={{
                                    page: pageContracts,
                                    limit: limitContracts,
                                    totalPages:
                                        totalPagesContracts
                                }}
                                onOrderBy={order =>
                                    handleOrderByContracts(
                                        order as unknown as ContractsOrderBy
                                    )
                                }
                                onAction={
                                    handleActionContracts
                                }
                                onChangePage={
                                    handlePageContracts
                                }
                                onChangeLimit={
                                    handleLimitContracts
                                }
                            />
                        </Box>
                    </TabPanelCustom>

                    <TabPanelCustom data-testid="institution-tab-panel-contract">
                        <Lotes
                            pageNumber={totalPagesLote}
                            dataLote={loteData}
                            limit={limitLote}
                            page={pageLote}
                            filter={filterLotes}
                            handleModalLote={
                                handleAddModalOpen
                            }
                            handleLimitLote={
                                handleLimitLote
                            }
                            handlePageLote={
                                handlePageLote
                            }
                        />
                    </TabPanelCustom>

                    <TabPanelCustom data-testid="institution-tab-panel-image">
                        <Lotes
                            pageNumber={totalPagesLote}
                            dataLote={loteData}
                            limit={limitLote}
                            page={pageLote}
                            type="image"
                            filter={filterLotes}
                            handleModalLote={
                                handleAddModalOpen
                            }
                            handleLimitLote={
                                handleLimitLote
                            }
                            handlePageLote={
                                handlePageLote
                            }
                        />
                    </TabPanelCustom>
                </TabPanels>
            </Tabs>
        </>
    )
}

export default Contracts
